import { useRouter } from 'next/router';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { TrackPageViewContainer } from '../../../../common/containers/TrackPageViewContainer';
import { getDehydratedState } from '../../../../common/getDehydrateState';
import { requestContext } from '../../../../common/requestContext';
import { DehydratedProps } from '../../../../common/type';
import { useSelectListAndRestData } from '../../../../common/useSelectListAndRestData.hook';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import { Loading } from '../../../../design-system/atoms/Loading/Loading';
import { Layout } from '../../../../design-system/layout/Layout';
import { LandingList } from '../../../../design-system/pages/Landing/LandingList';
import { getLandingListQuery } from '../../../../domain/landing/util/getLandingListQuery';
import { addError } from '../../../../errors/ErrorSlice';
import { thunkSerializeError } from '../../../../errors/thunkSerializeError.function';
import { landingFactory } from '../../../../factory/landingFactory';
import { LandingType, SeriesPosterViewLandingListQuery, SeriesPosterViewLandingListQueryVariables } from '../../../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../../../header/constants';
import { setHeader } from '../../../../header/HeaderSlice';
import { CACHE_ONLY_CONFIG } from '../../../../reactQuery/constant';
import { createQueryClient } from '../../../../reactQuery/createQueryClient';
import { nextReduxWrapper } from '../../../../state/store';
import { useInfiniteSeriesPosterViewLandingListQuery } from '../../../../wrapper';
interface SeriesPosterViewLandingListPageProps extends DehydratedProps {
  title: string;
  themeKeywordUid?: string;
}
function SeriesPosterViewLandingListPage({
  title,
  hasErrorQuery
}: SeriesPosterViewLandingListPageProps) {
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const {
    dataKey = '',
    themeKeywordUid
  } = getLandingListQuery(router.query);
  const seriesPosterViewLandingListVariables = useMemo(() => ({
    dataKey,
    themeKeywordUid,
    size: PAGE_SIZE
  }), [dataKey, themeKeywordUid]);
  const {
    data: infiniteData,
    fetchNextPage,
    isLoading,
    isSuccess,
    isFetching
  } = useInfiniteSeriesPosterViewLandingListQuery(seriesPosterViewLandingListVariables, {
    initialPageParam: {
      ...seriesPosterViewLandingListVariables,
      page: 0
    },
    getNextPageParam: lastPage => {
      return lastPage.seriesPosterViewLandingList?.isEnd ? undefined : {
        ...seriesPosterViewLandingListVariables,
        page: (lastPage.seriesPosterViewLandingList?.page || 0) + 1
      };
    },
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: !hasErrorQuery
  });
  const data = useSelectListAndRestData<SeriesPosterViewLandingListQuery, 'seriesPosterViewLandingList'>('seriesPosterViewLandingList', infiniteData);
  const onClickThemeKeyword = useCallback((keywordUid: string) => {
    if (keywordUid === themeKeywordUid) {
      router.replace(`/landing/series/poster/${dataKey}`);
      return;
    }
    router.replace(`/landing/series/poster/${dataKey}/theme/${keywordUid}`);
  }, [dataKey, router, themeKeywordUid]);
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  useEffect(() => {
    if (themeKeywordUid) {
      dispatch(setHeader({
        customTiaraObject: {
          customProps: {
            page_keyword: data?.themeKeywordOptList?.find(item => item.param === themeKeywordUid)?.name
          }
        }
      }));
    }
  }, [data?.themeKeywordOptList, dispatch, themeKeywordUid]);
  return <>
      {isLoading && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex-center-full">
          <Loading />
        </div>}

      {isSuccess && <LandingList themeKeywordList={data?.themeKeywordOptList} landingItemList={data?.list ?? []} selectedThemeKeywordUid={data?.selectedThemeKeywordOpt?.param} onClickThemeKeyword={onClickThemeKeyword} isMobile={isMobile} isWebView={isWebView} landingType={LandingType.SeriesPoster} title={title} isLoading={isFetching} topImageUrl={data?.topImage} totalCount={data?.totalCount ?? 0} isEnd={data?.isEnd} requestNextPageData={() => fetchNextPage()} />}
      <TrackPageViewContainer customPropsDataKeyList={[]} trackObj={{
      section: '랜딩리스트',
      page: '랜딩리스트',
      actionType: 'Pageview',
      actionName: '리스트_화면',
      page_meta: {
        id: dataKey,
        type: 'reference',
        name: title,
        category: data?.category
      },
      customProps: {
        user_action: 'pv',
        ...(themeKeywordUid !== undefined ? {
          page_keyword: themeKeywordUid
        } : {}),
        view_type: 'series_poster_view'
      }
    }} />
    </>;
}
SeriesPosterViewLandingListPage.getInitialProps = nextReduxWrapper.getInitialPageProps(store => {
  return async ({
    query,
    req
  }): Promise<SeriesPosterViewLandingListPageProps> => {
    let title = '';
    const {
      dataKey,
      themeKeywordUid
    } = getLandingListQuery(query);
    if (dataKey) {
      try {
        const queryClient = createQueryClient();
        const variables = {
          dataKey,
          themeKeywordUid,
          size: PAGE_SIZE
        } as SeriesPosterViewLandingListQueryVariables;
        const data = await queryClient.fetchInfiniteQuery(landingFactory.infiniteSeriesPosterViewLandingList(variables, {
          initialPageParam: variables,
          getNextPageParam: () => undefined,
          ...CACHE_ONLY_CONFIG
        }, {
          ...(req && {
            ssrRequest: req
          })
        }));
        title = data.pages[0].seriesPosterViewLandingList?.title || '';
        return {
          title,
          themeKeywordUid,
          ...getDehydratedState(queryClient)
        };
      } catch (error) {
        store.dispatch(addError({
          error: thunkSerializeError(error)
        }));
        return {
          title,
          hasErrorQuery: true
        };
      }
    }
    return {
      title
    };
  };
});
const PAGE_SIZE = 25;
SeriesPosterViewLandingListPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={page.props.title} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true,
    headerType: CommonMobileHeaderType.TwoDepth
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true
  }}>
      {page}
    </Layout>;
};
export default SeriesPosterViewLandingListPage;